import { useState, useEffect, useCallback, UIEvent, FC, useRef } from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import Checkbox from "@mui/material/Checkbox";
import DataObjectIcon from "@mui/icons-material/DataObject";
import CachedIcon from "@mui/icons-material/Cached";

import "./style.scss";
import { CitationDisplay } from "../../../common/enums/citation-display.enum";
import { CITATION_DISPLAY_LABELS } from "../../../common/utilities/citation-display.util";
import { ReactComponent as CheckboxIcon } from "../../../assets/checkbox.icon.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../../assets/checkbox-checked.icon.svg";
import { ReactComponent as ChevronRightIcon } from "../../../assets/chevron-right.icon.svg";
import { ReactComponent as ChevronDownIcon } from "../../../assets/chevron-down.icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete.icon.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/download.icon.svg";
import { ReactComponent as RefreshIcon } from "../../../assets/refresh.icon.svg";
import { ReactComponent as PlaceholderIcon } from "../../../assets/placeholder--loader.icon.svg";
import { RecognitionResult } from "../../../common/interfaces/admin/recognition-result.interface";
import { TableLearningObject } from "../../../common/interfaces/admin/learning-object.interface";
import {
    RecognitionResultStatus,
    RRSwitchStatus
} from "../../../common/enums/recognition-result-status.enum";
import { LearningObjectStatus } from "../../../common/enums/learning-object-status.enum";
import { SourceType } from "../../../common/enums/source-type.enum";
import { formatStringDate } from "../../../common/utilities/dates/date-formater.util";
import { Loader } from "../../../components/loader/loader.comp";
import { SelectionFilter } from "../../../components/menu-filter/menu-filter.comp";

const LearningObjects = ({
    totalCount,
    items,
    setItems,
    checkedItems,
    setCheckedItems,
    selectionMode = false,
    placeholderIcon = PlaceholderIcon,
    placeholderText = "So empty",
    placeholderDescription = "There are no files to show",
    placeholderButtonTitle,
    placeholderButtonIcon,
    placeholderAction,
    scrollUpCounter,
    handleAppendFiles,
    handleChangePublishStatus,
    handleAIResult,
    handleDelete,
    handleDownload,
    handleForceReprocess,
    handleReindex,
    handleViewChunks,
    onEditVisibility,
    isSuperAdmin = false
}: {
    totalCount?: number;
    items: TableLearningObject[];
    setItems: (arg1: TableLearningObject[]) => void;
    checkedItems: TableLearningObject[];
    setCheckedItems: (arg1: TableLearningObject[]) => void;
    selectionMode?: boolean;
    scrollUpCounter?: number;
    placeholderIcon?: FC<React.SVGProps<SVGSVGElement>>;
    placeholderText?: string;
    placeholderDescription?: string;
    placeholderButtonTitle?: string;
    placeholderButtonIcon?: FC<React.SVGProps<SVGSVGElement>>;
    placeholderAction?: () => void;
    handleAppendFiles?: () => void;
    handleChangePublishStatus: (arg1: RecognitionResult, arg2: string) => void;
    handleAIResult: (arg1: string) => void;
    handleDelete?: (arg1: string[]) => void;
    handleDownload?: (arg1: TableLearningObject) => void;
    handleForceReprocess?: (arg1: TableLearningObject) => void;
    handleReindex?: (arg1: TableLearningObject) => void;
    handleViewChunks?: (arg1: TableLearningObject) => void;
    onEditVisibility?: (arg1: TableLearningObject) => void;
    isSuperAdmin?: boolean;
}) => {
    const scrollDelata = 250;
    const scrollRef = useRef<HTMLDivElement>(null);
    const [appendAvailable, setAppendAvailable] = useState<boolean>(true);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [rrStatusList] = useState<string[]>(Object.values(RRSwitchStatus));

    useEffect(() => {
        if (scrollUpCounter) {
            scrollRef.current?.scrollTo(0, 0);
        }
    }, [scrollUpCounter]);

    useEffect(() => {
        setShowLoader(!!handleAppendFiles && !!totalCount && totalCount !== items.length);
    }, [items, totalCount, handleAppendFiles]);

    const handleChangeSelectAll = useCallback(() => {
        setCheckedItems(items.length === checkedItems.length ? [] : items);
    }, [items, checkedItems, setCheckedItems]);

    const handleChangeSelect = useCallback(
        (item: TableLearningObject) => {
            // Toggle by loItemId, not object reference: after a list refresh the
            // row objects are new instances while checkedItems may still hold the
            // old references, so reference equality can't find them to deselect.
            const isChecked = checkedItems.some(ci => ci.loItemId === item.loItemId);
            setCheckedItems(
                isChecked
                    ? checkedItems.filter(ci => ci.loItemId !== item.loItemId)
                    : [...checkedItems, item]
            );
        },
        [checkedItems, setCheckedItems]
    );

    const isSelectedItem = useCallback(
        (item: TableLearningObject) => {
            return checkedItems.map(item => item.loItemId).includes(item.loItemId);
        },
        [checkedItems]
    );

    const isSupportedSourceType = (sourceType: string): boolean => {
        return !!Object.values(SourceType).find(type => type === sourceType);
    };

    const loStatusColor = (item: TableLearningObject) => {
        if (!isSupportedSourceType(item.sourceType)) {
            return "yellow";
        }

        switch (item.status as LearningObjectStatus) {
            case LearningObjectStatus.inUse:
                return "green";
            case LearningObjectStatus.idle:
            case LearningObjectStatus.converting:
            case LearningObjectStatus.recognizing:
            case LearningObjectStatus.transcribing:
                return "yellow";
            case LearningObjectStatus.error:
            case LearningObjectStatus.waitingForDownload:
            case LearningObjectStatus.waitingForRecognition:
                return "red";
            default:
                return "default";
        }
    };

    const loStatusName = (item: TableLearningObject) => {
        if (!isSupportedSourceType(item.sourceType)) {
            return "Unsupported file";
        }

        switch (item.status as LearningObjectStatus) {
            case LearningObjectStatus.inUse:
                return "In Use";
            case LearningObjectStatus.idle:
                return "Idle";
            case LearningObjectStatus.converting:
                return "Converting";
            case LearningObjectStatus.recognizing:
                return "Recognizing";
            case LearningObjectStatus.transcribing:
                return "Transcribing";
            case LearningObjectStatus.error:
                return "Error";
            case LearningObjectStatus.waitingForDownload:
                return "Waiting for download";
            case LearningObjectStatus.waitingForRecognition:
                return "Waiting for recognition";
            default:
                return item.status;
        }
    };

    const rrName = (rr: RecognitionResult) => {
        return `${rr.name} v.${rr.version}${rr.hasUpdates ? " (Edited)" : ""}`;
    };

    const isAIResultAvailable = (status: string) => {
        return (
            status === RecognitionResultStatus.active ||
            status === RecognitionResultStatus.published ||
            status === RecognitionResultStatus.recognized ||
            status === RecognitionResultStatus.generatingSummary ||
            status === RecognitionResultStatus.waitingForSummary
        );
    };

    const handleLOClick = useCallback(
        (item: TableLearningObject) => {
            setItems(
                items.map(lo =>
                    lo.loItemId === item.loItemId ? { ...lo, showRR: !lo.showRR } : lo
                )
            );
        },
        [items, setItems]
    );

    const changePublishStatus = (status: string, loName: string, rr: RecognitionResult) => {
        if (rr.isPublish !== (status === RRSwitchStatus.published)) {
            handleChangePublishStatus(rr, loName);
        }
    };

    const handleAIResultClick = (uid: string) => {
        handleAIResult(uid);
    };

    const handleDeleteClick = (item: TableLearningObject) => {
        if (handleDelete) {
            handleDelete([item.loItemId]);
        }
    };

    const handleDownloadClick = (item: TableLearningObject) => {
        if (handleDownload) {
            handleDownload(item);
        }
    };

    const handleListScroll = (event: UIEvent<HTMLElement>) => {
        if (!handleAppendFiles || (totalCount && totalCount === items.length)) return;

        const scrollBottom =
            event.currentTarget.scrollHeight -
            (event.currentTarget.scrollTop + event.currentTarget.offsetHeight);

        if (appendAvailable && scrollBottom < scrollDelata) {
            setAppendAvailable(false);
            handleAppendFiles();
        } else if (scrollBottom >= scrollDelata) {
            setAppendAvailable(true);
        }
    };

    return (
        <div className="lo-table-view">
            {items.length ? (
                <div className="lo-table" role="table" aria-label="Files">
                    <div className="lo-table-header" role="row">
                        {selectionMode && (
                            <div
                                className="lo-table-header-cell lo-table-cell__select"
                                role="columnheader"
                            >
                                <Checkbox
                                    checked={items.length === checkedItems.length}
                                    onChange={handleChangeSelectAll}
                                    icon={<CheckboxIcon />}
                                    checkedIcon={<CheckboxCheckedIcon />}
                                    inputProps={{ "aria-label": "Select all files" }}
                                />
                            </div>
                        )}
                        <div
                            className="lo-table-header-cell lo-table-cell__name"
                            role="columnheader"
                        >
                            File Name
                        </div>
                        <div
                            className="lo-table-header-cell lo-table-cell__date"
                            role="columnheader"
                        >
                            Date
                        </div>
                        <div
                            className="lo-table-header-cell lo-table-cell__status"
                            role="columnheader"
                        >
                            Status
                        </div>
                        {onEditVisibility && (
                            <div
                                className="lo-table-header-cell lo-table-cell__visibility"
                                role="columnheader"
                            >
                                Visibility
                            </div>
                        )}
                        <div
                            className="lo-table-header-cell lo-table-cell__action"
                            role="columnheader"
                        ></div>
                    </div>
                    <div
                        className="lo-table-body"
                        ref={scrollRef}
                        onScroll={handleListScroll}
                        role="rowgroup"
                    >
                        {items.map(item => (
                            <div
                                key={item.loItemId}
                                className="lo-table-row-block"
                                role="presentation"
                            >
                                <div className="lo-table-row" role="row">
                                    {selectionMode && (
                                        <div
                                            className="lo-table-cell lo-table-cell__select"
                                            role="cell"
                                        >
                                            <Checkbox
                                                checked={isSelectedItem(item)}
                                                onChange={() => handleChangeSelect(item)}
                                                icon={<CheckboxIcon />}
                                                checkedIcon={<CheckboxCheckedIcon />}
                                                inputProps={{
                                                    "aria-label": `Select ${item.name}`
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div
                                        className={`lo-table-cell lo-table-cell__name${!selectionMode ? " lo-table-cell__name--first" : ""}`}
                                        role="rowheader"
                                    >
                                        <div
                                            className="lo-name"
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Toggle details for ${item.name}`}
                                            aria-expanded={!!item.showRR}
                                            onClick={() => handleLOClick(item)}
                                            onKeyDown={e => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    handleLOClick(item);
                                                }
                                            }}
                                        >
                                            <SvgIcon
                                                className="lo-name__icon"
                                                component={
                                                    item.showRR ? ChevronDownIcon : ChevronRightIcon
                                                }
                                                inheritViewBox
                                            />
                                            <span className="lo-name__text">{item.name}</span>
                                        </div>
                                    </div>
                                    <div className="lo-table-cell lo-table-cell__date" role="cell">
                                        <span className="lo-date">
                                            {formatStringDate(item.createdAt, "MM.dd.yyyy")}
                                        </span>
                                    </div>
                                    <div
                                        className="lo-table-cell lo-table-cell__status"
                                        role="cell"
                                    >
                                        {isSuperAdmin &&
                                        item.status === LearningObjectStatus.error &&
                                        item.statusMessage ? (
                                            <Tooltip title={item.statusMessage} placement="top">
                                                <div
                                                    className={`lo-status lo-status--${loStatusColor(item)}`}
                                                >
                                                    <div className="lo-status__circle"></div>
                                                    <span className="lo-status__title">
                                                        {loStatusName(item)}
                                                    </span>
                                                </div>
                                            </Tooltip>
                                        ) : (
                                            <div
                                                className={`lo-status lo-status--${loStatusColor(item)}`}
                                            >
                                                <div className="lo-status__circle"></div>
                                                <span className="lo-status__title">
                                                    {loStatusName(item)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {onEditVisibility && (
                                        <div
                                            className="lo-table-cell lo-table-cell__visibility"
                                            role="cell"
                                        >
                                            <Tooltip title="Edit visibility" placement="top">
                                                <div
                                                    className="lo-visibility"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => onEditVisibility?.(item)}
                                                    onKeyDown={e => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            onEditVisibility?.(item);
                                                        }
                                                    }}
                                                >
                                                    <span className="visually-hidden">
                                                        Edit visibility,{" "}
                                                    </span>
                                                    <span
                                                        className={`lo-visibility__chip lo-visibility__chip--${(
                                                            item.effectiveCitationDisplay ??
                                                            CitationDisplay.pagesLinked
                                                        ).toLowerCase()}${
                                                            item.citationDisplay == null
                                                                ? " lo-visibility__chip--inherited"
                                                                : ""
                                                        }`}
                                                    >
                                                        {
                                                            CITATION_DISPLAY_LABELS[
                                                                (item.effectiveCitationDisplay ??
                                                                    CitationDisplay.pagesLinked) as CitationDisplay
                                                            ]
                                                        }
                                                    </span>
                                                </div>
                                            </Tooltip>
                                        </div>
                                    )}
                                    <div
                                        className="lo-table-cell lo-table-cell__action"
                                        role="cell"
                                    >
                                        {isSuperAdmin && handleForceReprocess && (
                                            <Tooltip title="Reprocess" placement="top">
                                                <IconButton
                                                    className="lo-action__button-reprocess"
                                                    onClick={() => handleForceReprocess(item)}
                                                >
                                                    <SvgIcon
                                                        component={RefreshIcon}
                                                        inheritViewBox
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {isSuperAdmin && handleReindex && (
                                            <Tooltip title="Reindex" placement="top">
                                                <IconButton
                                                    className="lo-action__button-reindex"
                                                    onClick={() => handleReindex(item)}
                                                >
                                                    <CachedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {isSuperAdmin && handleViewChunks && (
                                            <Tooltip title="View Chunks" placement="top">
                                                <IconButton
                                                    className="lo-action__button-chunks"
                                                    onClick={() => handleViewChunks(item)}
                                                >
                                                    <DataObjectIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {selectionMode && handleDelete && (
                                            <Tooltip title="Delete" placement="top">
                                                <IconButton
                                                    className="lo-action__button-delete"
                                                    onClick={() => handleDeleteClick(item)}
                                                >
                                                    <SvgIcon
                                                        component={DeleteIcon}
                                                        inheritViewBox
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {handleDownload && (
                                            <Tooltip title="Download" placement="top">
                                                <IconButton
                                                    className="lo-action__button-download"
                                                    onClick={() => handleDownloadClick(item)}
                                                >
                                                    <SvgIcon
                                                        component={DownloadIcon}
                                                        inheritViewBox
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                                {!!item.recognitionResults.length &&
                                    item.recognitionResults.map(rr => (
                                        <div
                                            key={rr.uid}
                                            className={`lo-table-row lo-table-row--rr${!item.showRR ? " lo-table-row--rr-hidden" : ""}`}
                                            role="row"
                                        >
                                            {selectionMode && (
                                                <div
                                                    className="lo-table-cell lo-table-cell__select"
                                                    role="cell"
                                                ></div>
                                            )}
                                            <div
                                                className={`lo-table-cell lo-table-cell__name${!selectionMode ? " lo-table-cell__name--first" : ""}`}
                                                role="rowheader"
                                            >
                                                <div className="lo-name">
                                                    <span className="lo-name__text">
                                                        {rrName(rr)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div
                                                className="lo-table-cell lo-table-cell__date"
                                                role="cell"
                                            >
                                                <span className="lo-date">
                                                    {formatStringDate(
                                                        rr.createdAt,
                                                        "MM.dd.yyyy — hh:mm tt"
                                                    )}
                                                </span>
                                            </div>
                                            <div
                                                className="lo-table-cell lo-table-cell__status"
                                                role="cell"
                                            >
                                                <SelectionFilter
                                                    list={rrStatusList}
                                                    selectedItem={
                                                        rr.isPublish
                                                            ? RRSwitchStatus.published
                                                            : RRSwitchStatus.off
                                                    }
                                                    setSelectedItem={status =>
                                                        changePublishStatus(status, item.name, rr)
                                                    }
                                                    rightPosition={true}
                                                />
                                            </div>
                                            {onEditVisibility && (
                                                <div
                                                    className="lo-table-cell lo-table-cell__visibility"
                                                    role="cell"
                                                ></div>
                                            )}
                                            <div
                                                className="lo-table-cell lo-table-cell__action"
                                                role="cell"
                                            >
                                                <Button
                                                    className="lo-action__button-result secondary-button"
                                                    variant="text"
                                                    disabled={!isAIResultAvailable(rr.status)}
                                                    onClick={() => handleAIResultClick(rr.uid)}
                                                >
                                                    {isAIResultAvailable(rr.status)
                                                        ? "AI Result"
                                                        : "Loading"}
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        ))}
                        {showLoader && (
                            <div className="lo-table-row lo-table-row__loader">
                                <Loader size={24} borderWidth={4} />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="lo-table-placeholder">
                    <div className="lo-table-placeholder-content">
                        <SvgIcon
                            className="lo-table-placeholder-content__icon"
                            component={placeholderIcon}
                            inheritViewBox
                        />
                        <span className="lo-table-placeholder-content__title">
                            {placeholderText}
                        </span>
                        <span className="lo-table-placeholder-content__description">
                            {placeholderDescription}
                        </span>
                        {placeholderButtonTitle && placeholderAction && (
                            <Button
                                className="primary-button--icon-first lo-table-placeholder-content__button"
                                variant="text"
                                startIcon={
                                    placeholderButtonIcon ? (
                                        <SvgIcon
                                            className="primary-button--icon-first__icon"
                                            component={placeholderButtonIcon}
                                            inheritViewBox
                                        />
                                    ) : undefined
                                }
                                onClick={placeholderAction}
                            >
                                <span className="primary-button--icon-first__text">
                                    {placeholderButtonTitle}
                                </span>
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LearningObjects;
